import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRightCircle, Users, LayoutGrid, ShieldCheck, Banknote, HeartPulse,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { NAVIGATION_BY_ROLE, PRODUCT_SUITES } from '../../lib/caraloom_v2_ia';

// Map the IA's role labels (facility/provider/clinician/family/admin) to
// our existing user.role values used by ProtectedRoute + ROLE_TO_DASHBOARD.
// `agency` and `discharge_planner` are pre-pivot terms; we keep them as
// aliases so this component works without database migration.
const APP_ROLE_TO_IA_ROLE = {
  discharge_planner: 'facility',
  agency: 'provider',
  clinician: 'clinician',
  patient: 'family',
  // Payer is intentionally OMITTED — the payer role has exactly one route
  // (`/payer/dashboard`) in App.js. Rendering a Suites bar would route
  // them to admin surfaces that ProtectedRoute rejects. `SuiteNav` returns
  // null when no IA mapping is found, which is the correct behavior for
  // single-route roles.
  admin: 'admin',
};

// Map suite id → primary route path. Each suite resolves to the role's
// canonical landing page for that suite.
const SUITE_ROUTE_BY_ROLE = {
  facility: {
    referrals: '/referrals/ingest-fhir',
    workforce: '/planner/dashboard',
  },
  provider: {
    operations:      '/operations/executive',
    referrals:       '/agency/dashboard',
    workforce:       '/agency/find-clinicians',
    compliance:      '/compliance/survey-readiness',
    'revenue-cycle': '/operations/pnl',
  },
  clinician: {
    workforce: '/clinician/dashboard',
    'revenue-cycle': '/clinician/denial-prediction',
    operations: '/operations/executive',
  },
  family: {
    care: '/patient/dashboard',
  },
  admin: {
    operations:      '/operations/executive',
    referrals:       '/admin/dashboard',
    workforce:       '/admin/dashboard',
    compliance:      '/admin/safety-shield',
    'revenue-cycle': '/admin/payments',
    care:            '/admin/dashboard',
  },
};

// Lucide icon component map keyed by the suite.icon string in caraloom_v2_ia.js
const ICON_MAP = {
  'arrow-right-circle': ArrowRightCircle,
  'users':              Users,
  'layout-grid':        LayoutGrid,
  'shield-check':       ShieldCheck,
  'banknote':           Banknote,
  'heart-pulse':        HeartPulse,
};

/**
 * SuiteNav — role-scoped sub-navigation for authenticated users.
 *
 * Renders the suite links from NAVIGATION_BY_ROLE (see caraloom_v2_ia.js)
 * for the current user's role. Designed as a horizontal pill bar that sits
 * directly below the global PublicNavbar inside the authenticated app shell.
 * Hidden entirely on public/marketing pages and when no user is signed in.
 *
 * The bar lights up the active suite based on a longest-prefix match against
 * SUITE_ROUTE_BY_ROLE[role][suite]. The non-primary suite links appear in a
 * neutral style; the primary suite gets the brand accent.
 */
export const SuiteNav = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading || !user) return null;
  const iaRole = APP_ROLE_TO_IA_ROLE[user.role];
  if (!iaRole) return null;
  const navItems = NAVIGATION_BY_ROLE[iaRole] || [];
  if (navItems.length === 0) return null;

  const routes = SUITE_ROUTE_BY_ROLE[iaRole] || {};
  // Active suite: the longest matching prefix wins.
  const activeSuite = navItems
    .map((item) => ({ suite: item.suite, route: routes[item.suite] || '' }))
    .filter((r) => r.route && pathname.startsWith(r.route))
    .sort((a, b) => b.route.length - a.route.length)[0]?.suite;

  return (
    <div
      className="border-b border-cn-border bg-cn-bone-100/50 dark:bg-cn-ink-900/40 backdrop-blur-sm"
      data-testid="suite-nav"
    >
      <div className="cn-container py-2 flex flex-wrap items-center gap-1.5 overflow-x-auto">
        <span className="text-[10px] font-bold tracking-widest uppercase text-cn-text-muted pr-2">
          Suites
        </span>
        {navItems.map(({ suite, label, primary }) => {
          const meta = PRODUCT_SUITES.find((s) => s.id === suite);
          if (!meta) return null;
          const route = routes[suite] || '/';
          const Icon = ICON_MAP[meta.icon] || LayoutGrid;
          const isActive = activeSuite === suite;
          const colorClass = isActive
            ? 'bg-cn-primary text-white shadow-sm'
            : primary
            ? 'bg-white dark:bg-cn-ink-800 text-cn-text border border-cn-border hover:border-cn-primary'
            : 'text-cn-text-muted hover:text-cn-text hover:bg-cn-bone-200/70';
          return (
            <Link
              key={suite}
              to={route}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${colorClass}`}
              data-testid={`suite-nav-${suite}`}
              title={meta.tagline}
            >
              <Icon className="w-3.5 h-3.5" aria-hidden="true" />
              {label}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SuiteNav;
